import type {
  NameAccessor,
  TreeMapClusterNode,
  TreeMapFlatProps,
  TreeMapHierarchicalProps,
  TreeMapLeafNode,
  TreeMapRootNode,
} from '../types/treemap.js';

export const toHierarchicalProps = (
  props: TreeMapFlatProps,
  // eslint-disable-next-line @typescript-eslint/no-deprecated
): TreeMapHierarchicalProps => {
  const { data, valueAccessor, nameAccessor, groupBy, ...rest } = props;

  const { rootKey, clusterKey, leafKey } = resolveGroupKeys(groupBy);
  const rootName = resolveRootName(data, rootKey);

  const clusterMap = buildClusterMap(data, valueAccessor, clusterKey, leafKey);

  const nodes: TreeMapClusterNode[] = buildClusterNodes(clusterMap);
  const tree = buildRootNode(nodes, rootName);

  return {
    ...rest,
    data: { tree },
    nameAccessor: resolveNameAccessor(data, nameAccessor),
  };
};

const resolveNameAccessor = (
  data: Record<string, unknown>[],
  nameAccessor: string | undefined,
): NameAccessor | undefined => {
  if (nameAccessor === undefined) {
    return undefined;
  }
  return typeof data[0]?.[nameAccessor] === 'number' ? 'value' : 'name';
};

const resolveGroupKeys = (groupBy: string | string[]) => {
  const groups = Array.isArray(groupBy) ? groupBy.slice(0, 3) : [groupBy];
  const hasRoot = groups.length >= 3;

  return {
    rootKey: hasRoot ? groups[0] : undefined,
    clusterKey: hasRoot ? groups[1] : groups[0],
    leafKey: groups.length >= 2 ? groups.at(-1) : undefined,
  };
};

const buildRootNode = (
  nodes: TreeMapClusterNode[],
  rootName: string | undefined,
): TreeMapRootNode => ({
  nodes,
  value: nodes.reduce((sum, n) => sum + n.value, 0),
  ...(rootName === undefined ? {} : { name: rootName }),
});

const buildClusterNodes = (
  clusterMap: Map<string, { leaves: TreeMapLeafNode[]; value: number }>,
): TreeMapClusterNode[] =>
  Array.from(clusterMap.entries()).map(([name, { leaves, value }]) => ({
    name,
    value,
    ...(leaves.length > 0 ? { nodes: leaves } : {}),
  }));

const buildClusterMap = (
  data: Record<string, unknown>[],
  valueAccessor: string,
  clusterKey: string,
  leafKey: string | undefined,
) => {
  const clusterMap = new Map<
    string,
    { leaves: TreeMapLeafNode[]; value: number }
  >();

  for (const dp of data) {
    const rawValue = dp[valueAccessor];
    if (typeof rawValue !== 'number') {
      continue;
    }

    const clusterName = toSafeString(dp[clusterKey]);

    let cluster = clusterMap.get(clusterName);
    if (!cluster) {
      cluster = {
        leaves: [] as TreeMapLeafNode[],
        value: 0,
      };
      clusterMap.set(clusterName, cluster);
    }

    cluster.value += rawValue;

    if (leafKey === undefined) {
      continue;
    }
    const leafName = toSafeString(dp[leafKey]);
    cluster.leaves.push({ name: leafName, value: rawValue });
  }

  return clusterMap;
};

const resolveRootName = (
  data: Record<string, unknown>[],
  rootKey: string | undefined,
): string | undefined => {
  if (rootKey === undefined) {
    return undefined;
  }

  const rootNameValue = data.find(
    (dp) => dp[rootKey] !== null && dp[rootKey] !== undefined,
  )?.[rootKey];

  if (rootNameValue === null || rootNameValue === undefined) {
    return undefined;
  }

  return toSafeString(rootNameValue);
};

const toSafeString = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return `${value}`;
  }
  return '';
};
